import React from 'react';
import { ThemeProvider, withStyles, WithStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import Hidden from '@material-ui/core/Hidden';
import Navigator from './Navigator';
import { MuiStyles } from './styling/MuiStyles';
import ProfileContent, { ProfileDetails } from './ProfileContent';
import LicensesContent, { DealCollection, DealType, EVALUATIONS_DEAL_IDENTIFIER, EVALUATIONS_LICENSE_TYPE, SUBSCRIPTIONS_LICENSE_TYPE } from './LicensesContent';
import SaaSContent, { initDealIdCollection, resetSaasTrialStatus, SaaSTrialStatus } from './SaaSContent';
import { AuthContext } from '../authentication/AuthContext';
import { CapabilitiesData, DealLineCollection, InitViewDealCollection, InnerLineCollection, LinesData, ViewDealLineCollection } from './DealsContext';
import { LineSystemCollection, RawSystemsData, SystemButton, SystemButtonState, SystemButtonStateCollection, SystemCollection, SystemsData } from './SystemsContext';
import HomeContent from './HomeContent';
import { SystemActivationData } from './ActivateLicense';
import { Utilities } from '../general/utilities';
import { getLicenseStringForHost, HostType, LicenseType, splitHostString } from './ActivateLicenseData';
import Header from './Header';
import { DownloadProductTypes, getDetails, ProductsDataSet } from './DownloadProductData';
import DownloadProduct from './DownloadProduct';
import DownloadLegacyProduct from './DownloadLegacyProduct';
import ProductInfoPage from './ProductInfoPage';
import { NavigatorData, NavigatorEntry, ProductId, Products } from './NavigatorData';
import { Redirect } from 'react-router-dom';
import ErrorDialog from './ErrorDialog';
import { DateFormat } from '../general/datetime-formatter/DateFormat';
import { TimeFormat } from '../general/datetime-formatter/TimeFormat';
import { SubsTrialData } from './SaaSConfirmTrial';
import { initialSortViewData, SortData, SortView, SortViewData } from '../general/sorting/LicenseSortParams';
import {MenuItemFormatType, EvalData, EvalStatus} from './RequestEvaluation'
import { useHistory } from 'react-router'
import Cookies from 'universal-cookie';

enum ResponsePart {
  deal = '/deals/',
  line = '/lines/'
}

export enum LicenseRequestStatus {
  noItems,
  notYetRequested,
  inProgress,
  dealsOK,
  errorResponse
}

export enum PageType {
  Home,
  Profile,
  EvaluationManager,
  LicenseManager,
  SubscriptionManager,
  SaasProduct,
  DeltaJSONSaasProduct,
  downloadXmlCompare,
  downloadDitaCompare,
  downloadXMLDataCompare,
  downloadxmlDitaMerge,
  downloadXmlMerge,
  downloadDocBookCompare,
  downloadDeltaJson,
  downloadConversionQA,
  downloadLegacyProducts,
  downloadLinuxLicenseServer,
  downloadWindowsLicenseServer,
  downloadMacIntelLicenseServer,
  downloadMacARMLicenseServer,
  downloadHtmlCompare,
  downloadProducts,
  downloadDeltaNova,
  downloadDeltaInsights,
  downloadDeltaOxygenPlugin
}


export interface TrialUsageData {
  product: TrialProducts,
  trialAllowed: boolean,
  paidSubscriptionActive: boolean,
  trialEnd: string,
  trialStart: string
}

export type TrialProducts = ( ProductId.ccS1000D | ProductId.ccJATS | ProductId.ccBITS | ProductId.ccNISOSTS | ProductId.ccXSLFO);
export type LicenseRequestType = { status: LicenseRequestStatus, statusNumber?: number, text?: string };
export type DelayData = [url: string, index: number];

export enum SystemUpdateReason {
  RefreshSystemData,
  Activation,
  Deactivation,
  RefreshSystemActivationData
}

export class LicenseRequestData {
  public deals: LicenseRequestType = { status: LicenseRequestStatus.notYetRequested };
  public lines: LicenseRequestType = { status: LicenseRequestStatus.notYetRequested };
  public systems: LicenseRequestType = { status: LicenseRequestStatus.notYetRequested };
  public downloads: LicenseRequestType = { status: LicenseRequestStatus.notYetRequested };
}
const initProfileDetails: ProfileDetails = {
  givenName: "",
  familyName: "",
  compName: "",
  phone: "",
  emailAddress: "",
  dateFormat: DateFormat.Unix,
  timeFormat: TimeFormat.AmPm,
  avatarPicture: "",
  error: {
    phone: false
  }
}

export enum LinesView {
  CURRENT, // ((Issued=true AND Historic=false) AND Maintenance_Expiry_Date >= '" + currentDateUnix + "')
  HISTORIC, // (Historic=true OR Maintenance_Expiry_Date < '" + currentDateUnix + "')
  CURRENT_SUBS,
  HISTORIC_SUBS,
  CURRENT_EVALS,
  HISTORIC_EVALS
}

const delayForLinesConcurrency = 150; // ms
const delayForSystemConcurrency = 150; // ms


// using material-ui themes for styling Paperbase
const theme = MuiStyles.MuiTheme;
const drawerWidth = 256;
const styles = MuiStyles.createPaperBaseStyles(theme, drawerWidth);

// for testing delays
function sleep(ms: number) {
  return new Promise(resolve => {
    setTimeout(() => {
      return resolve(true);
    }, ms)
  });
}

export interface PaperbaseProps extends WithStyles<typeof styles> {
  pageType: PageType
};

export type IdSet = [dealId: string | null, lineId: string | null, systemId: string];

export type SystemCallbackType = (idSet: IdSet, actionType: SystemButton, data?: SystemActivationData) => void;

const appURL = process.env.REACT_APP_SERVER_API_URL!;

function Paperbase(props: PaperbaseProps) {
  const { classes, pageType } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [licenseResponseState, setLicenseResponseState] = React.useState(new LicenseRequestData());
  const [dealsData, setDealsData] = React.useState<DealCollection>({});
  const [dealsSubsData, setDealsSubsData] = React.useState<DealCollection>({});
  const [dealsEvalsData, setDealsEvalsData] = React.useState<DealCollection>({});
  const [lineData, setLineData] = React.useState<ViewDealLineCollection>(InitViewDealCollection());
  const [systemData, setSystemData] = React.useState<LineSystemCollection>({});
  const [systemButtons, setSystemButtons] = React.useState<SystemButtonStateCollection>({});
  const [profileDetails, setProfileDetails] = React.useState(initProfileDetails);
  const authContext = React.useContext(AuthContext);
  const [preUpdateProfileDetails, setPreUpdateProfileDetails] = React.useState(initProfileDetails);
  const [downloadProductsData, setDownloadProductsData] = React.useState<ProductsDataSet>({ products: [], license_servers: [] });
  const [errorDialog, setErrorDialog] = React.useState({ open: false, systemButton: SystemButton.activate, errorMessage: "" });
  const [licenseProgressNumber, setLicenseProgressNumber] = React.useState(0);
  const [linesView, setLinesView] = React.useState(LinesView.CURRENT);
  const [subsLinesView, setSubsLinesView] = React.useState(LinesView.CURRENT_SUBS);
  const [evalsLinesView, setEvalsLinesView] = React.useState(LinesView.CURRENT_EVALS);
  const [createSystemInProgress, setCreateSystemInProgress] = React.useState(false);
  const [saasTrialStatusCollection, setSaasTrialStatusCollection] = React.useState(resetSaasTrialStatus(SaaSTrialStatus.Unknown));
  const [saasDealIdCollection, setSaasDealIdCollection] = React.useState(initDealIdCollection());
  const [sortViewData, setSortViewData] = React.useState<SortViewData>(initialSortViewData());

  const enableExpiredViewOption = licenseResponseState.deals.status !== LicenseRequestStatus.inProgress &&
    licenseResponseState.lines.status !== LicenseRequestStatus.inProgress &&
    licenseResponseState.systems.status !== LicenseRequestStatus.inProgress &&
    !createSystemInProgress;

  // let tierDataFetchedOK = false;

  const onSessionUpdate = () => {
    let givenName: string, familyName: string, compName: string, phone: string, emailAddress: string, dateFormat: DateFormat, timeFormat: TimeFormat, avatarPicture: string;
    givenName = authContext.userProfile?.user_metadata === undefined ? authContext.authVariables?.name.split(" ")[0] : authContext.userProfile?.user_metadata.given_name;
    familyName = authContext.userProfile?.user_metadata === undefined ? authContext.authVariables?.name.split(/[ ](.+)/)[1] : authContext.userProfile?.user_metadata.family_name;
    const compNameExists = authContext.userProfile?.user_metadata?.company && authContext.userProfile.user_metadata.company.length > 0;
    compName = compNameExists ? authContext.userProfile?.user_metadata.company : "---";
    const phoneExists = authContext.userProfile?.user_metadata?.phone && authContext.userProfile.user_metadata.phone.length > 0;
    phone = phoneExists ? authContext.userProfile?.user_metadata.phone_number : "---";
    emailAddress = authContext.userProfile?.email ? authContext.userProfile?.email : "";
    dateFormat = authContext.userProfile?.user_metadata === undefined ? DateFormat.Unix : authContext.userProfile?.user_metadata.date_format;
    timeFormat = authContext.userProfile?.user_metadata === undefined ? TimeFormat.AmPm : authContext.userProfile?.user_metadata.timeFormat;
    avatarPicture = authContext.userProfile?.picture ? authContext.userProfile?.picture : "";
    setProfileDetails(prevProfileDetails => ({
      ...prevProfileDetails,
      givenName: givenName,
      familyName: familyName,
      compName: compName,
      phone: phone,
      emailAddress: emailAddress,
      dateFormat: dateFormat,
      timeFormat: timeFormat,
      avatarPicture: avatarPicture
    }));

    if (authContext.authVariables?.access_token && authContext.authVariables?.id_token) {
      const getRequestOptions: RequestInit = {
        method: 'GET',
        headers: {
          "Authorization": "Bearer " + authContext.authVariables?.access_token,
          "Accept": "application/json",
        }
      };
      
      updateSaasTrialState(getRequestOptions);
      getDeals(getRequestOptions);
      getUnactivatedSystem(getRequestOptions);
      getDownloadProductData(getRequestOptions);
      getAllCapabilities();
    }
  };

  const handleSaveProfileDetails = (event: React.MouseEvent<HTMLButtonElement>) => {
    authContext.updateUserMetadata(profileDetails);
    setProfileUpdateMode(false);
  }

  const phoneNumberRegex = '^[0-9-+ ]*$'
  const [updateProfileMode, setProfileUpdateMode] = React.useState(false);

  const handleUpdateProfileMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPreUpdateProfileDetails({ ...profileDetails });
    setProfileUpdateMode(true);
  }

  const handleCancelProfileMode = () => {
    setProfileUpdateMode(false);
    setProfileDetails({ ...preUpdateProfileDetails });
  }

  const handleProfileDetailsChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const controlName = event.target.name;
    const controlValue = event.target.value;
    if (controlName === "phone") {
      const errorOnPhoneNumber = controlValue.match(phoneNumberRegex) ? false : true;
      setProfileDetails(prevProfileDetails => ({
        ...prevProfileDetails,
        [controlName]: controlValue,
        error: { ...prevProfileDetails.error, [controlName]: errorOnPhoneNumber }
      }));
    } else {
      setProfileDetails(prevProfileDetails => ({ ...prevProfileDetails, [controlName]: controlValue }));
    }
  }

  authContext.onSessionUpdate = onSessionUpdate;

  const updateSystemButtonCollection = (idSet: IdSet, state: boolean, buttonType: SystemButton) => {
    const systemId = idSet[2];
    setSystemButtons((prevSystemButtonCollection) => {
      const copyOfPreviousCln = { ...prevSystemButtonCollection };
      let newState = new SystemButtonState();
      if (copyOfPreviousCln[systemId]) {
        newState = { ...copyOfPreviousCln[systemId] };
      }
      copyOfPreviousCln[systemId] = newState;
      newState[buttonType] = state;
      return copyOfPreviousCln;
    });
  }

  const authorizedRequestOptions: RequestInit = {
    method: 'GET',
    headers: {
      "Authorization": "Bearer " + authContext.authVariables?.access_token,
      "Accept": "application/json",
    }
  };

  const callSystemAction = (idSet: IdSet, actionType: SystemButton, data?: SystemActivationData) => {
    const [dealId, lineId, systemId] = idSet;
    const buttonStateForSystem = systemButtons[systemId];
    if (enableExpiredViewOption) {
      switch (actionType) {
        case SystemButton.activate:
          if (data && (buttonStateForSystem === undefined || buttonStateForSystem.activate)) {
            invokeSystemActivation(idSet, data);
          }
          break;
        case SystemButton.return:
          if (buttonStateForSystem === undefined || buttonStateForSystem.return) {
            invokeSystemDeActivation(idSet);
          }
          break;
        case SystemButton.history:
          if (buttonStateForSystem === undefined || buttonStateForSystem.history) {
            getActivations(idSet);
          }
          break;
      }
    }
  }

  const genericPostOptions: RequestInit = {
    method: "POST", headers: {
      "Authorization": "Bearer " + authContext.authVariables?.access_token,
      "Content-Type": "application/json",
    },
    body: ''
  };

  const handleChangeForLinesView = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isSubscriptionView = event.currentTarget.name === "subsRadioGroup";
    const isEvaluationView = event.currentTarget.name === "evalsRadioGroup";
    const updatedView = Number(event.currentTarget.value);
    if (isSubscriptionView) {
      setSubsLinesView(updatedView);
    } else if (isEvaluationView) {
      setEvalsLinesView(updatedView);
    } else {
      setLinesView(updatedView);
    }
  }

  const createPartialFulfillmentSystem = async (idSet: IdSet, fulfillmentSize: number) => {
    const [dealId, lineId, systemId] = idSet;

    const formData = {
      "fulfillmentSize": `${fulfillmentSize}`
    }

    const createPartialFulfillmentSystemOptions = { ...genericPostOptions, body: JSON.stringify(formData) };

    if (dealId && lineId) {
      setCreateSystemInProgress(true);
      const pathDealId = encodeURIComponent(dealId);
      const encodedURL = appURL + `/deals/${pathDealId}/lines/${lineId}/systems`;
      const createSystemResponse = await fetch(encodedURL, createPartialFulfillmentSystemOptions);
      if (createSystemResponse.ok) {
        try {
          const initialResult = await createSystemResponse.json();
          const serverSideSystemData = await getSystemDataFromServer(initialResult.systemId);
          setCreateSystemInProgress(false);
          if (serverSideSystemData) {
            updateSystemDataAfterPartialFulfillment(dealId, lineId, serverSideSystemData);
          } else {
            setErrorDialog({ open: true, errorMessage: `Unable to get data from created system`, systemButton: SystemButton.createSystem });
            console.error('createPartialFulfillmentSystem !response.ok', serverSideSystemData);
          }
        } catch (error) {
          setCreateSystemInProgress(false);
          console.error('createPartialFulfillmentSystem catch: error', error);
        }
      } else {
        setCreateSystemInProgress(false);
        const statusText = createSystemResponse.statusText ? `Text: ${createSystemResponse.statusText}` : '';
        setErrorDialog({ open: true, errorMessage: `Status: ${createSystemResponse.status} ${statusText}`, systemButton: SystemButton.createSystem });
        console.error('createPartialFulfillmentSystem !response.ok', createSystemResponse);
      }
    }
  }

  const updateSystemDataAfterPartialFulfillment = (dealId: string, lineId: string, systemData: RawSystemsData) => {

    setSystemData((prevLineSystemCollection: LineSystemCollection) => {
      const newLineSystemCollection: LineSystemCollection = { ...prevLineSystemCollection };

      const prevSystemCollection: SystemCollection = prevLineSystemCollection[Utilities.getFullId(dealId, lineId)];
      const newSystemCollection: SystemCollection = { ...prevSystemCollection };

      const prevSystemData = prevSystemCollection[systemData.systemId];
      const newSystemData = { ...prevSystemData };

      newSystemData.systemId = systemData.systemId;
      newSystemData.fulfillmentSize = systemData.fulfillmentSize;
      newSystemData.returnCount = systemData.current_return_count;
      newSystemData.returnCountLimit = systemData.return_count_limit;

      newSystemCollection[systemData.systemId] = newSystemData;
      const fullId = Utilities.getFullId(dealId, lineId);
      if (newLineSystemCollection[fullId]) {
        newLineSystemCollection[fullId] = newSystemCollection;
      }
      return newLineSystemCollection;
    })
  }

  const invokeSystemActivation = async (idSet: IdSet, data: SystemActivationData) => {
    const [dealId, lineId, systemId] = idSet;
    // port number included as part of request for License Model = Fixed Host REST
    const formData = data.port ?
      {
        "operation": "ACTIVATE",
        "hostid": getLicenseStringForHost(data.hostType, data.hostId),
        "hostid2": getLicenseStringForHost(data.hostType2, data.hostId2),
        "hostid3": getLicenseStringForHost(data.hostType3, data.hostId3),
        "port": data.port,
        "licenseId": data.licenseId
      } :
      {
        "operation": "ACTIVATE",
        "hostid": getLicenseStringForHost(data.hostType, data.hostId),
        "hostid2": getLicenseStringForHost(data.hostType2, data.hostId2),
        "hostid3": getLicenseStringForHost(data.hostType3, data.hostId3),
        "licenseId": data.licenseId
      }

    const activateOptions = { ...genericPostOptions, body: JSON.stringify(formData) };
    updateSystemButtonCollection(idSet, false, SystemButton.activate);

    fetch(appURL + `/systems/${systemId}/activations`, activateOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then(result => { updateSystemOnActivation(idSet, SystemUpdateReason.Activation, data) })
            .catch(error => console.error('activateLicense catch: error', error));
        } else {
          const statusText = response.statusText ? `Text: ${response.statusText}` : '';
          setErrorDialog({ open: true, errorMessage: `Status: ${response.status} ${statusText}`, systemButton: SystemButton.activate });
          console.error('activate !response.ok', response);
          updateSystemButtonCollection(idSet, true, SystemButton.activate);
        }

      }).catch((err) => {
        const statusText = err;
        setErrorDialog({ open: true, errorMessage: `Status: ${statusText}`, systemButton: SystemButton.activate });
        console.error('activate !response.ok', err);
        updateSystemButtonCollection(idSet, true, SystemButton.activate);
      });
  }

  const updateSystemOnActivation = (idSet: IdSet, reason: SystemUpdateReason, data: SystemActivationData) => {
    const [dealId, lineId, systemId] = idSet;

    const actionState = reason === SystemUpdateReason.Activation ?
      SystemButton.activate :
      reason === SystemUpdateReason.RefreshSystemActivationData ?
        SystemButton.history :
        SystemButton.return;

    updateSystemButtonCollection(idSet, true, actionState);

    if (dealId && lineId) {
      setSystemData((prevLineSystemCollection: LineSystemCollection) => {

        const newLineSystemCollection: LineSystemCollection = { ...prevLineSystemCollection };

        const prevSystemCollection: SystemCollection = prevLineSystemCollection[Utilities.getFullId(dealId, lineId)];
        const newSystemCollection: SystemCollection = { ...prevSystemCollection };

        const prevSystemData = prevSystemCollection[systemId];
        const newSystemData = { ...prevSystemData };

        switch (reason) {
          case SystemUpdateReason.RefreshSystemData:
          case SystemUpdateReason.Activation:
            newSystemData.hostIdType = data.hostType;
            newSystemData.hostId2Type = data.hostType2;
            newSystemData.hostId3Type = data.hostType3;
            newSystemData.hostId = data.hostId;
            newSystemData.hostId2 = data.hostId2;
            newSystemData.hostId3 = data.hostId3;
            break;
          case SystemUpdateReason.Deactivation:
            newSystemData.hostIdType = undefined;
            newSystemData.hostId2Type = undefined;
            newSystemData.hostId3Type = undefined;
            newSystemData.hostId = undefined;
            newSystemData.hostId2 = undefined;
            newSystemData.hostId3 = undefined;
            newSystemData.returnCount = data.returnCount;
            break;
          case SystemUpdateReason.RefreshSystemActivationData:
            if (data.rawActivations) {
              newSystemData.rawActivations = data.rawActivations;
            }
            break;
        }

        newSystemCollection[systemId] = newSystemData;
        const fullId = Utilities.getFullId(dealId, lineId);
        if (newLineSystemCollection[fullId]) {
          newLineSystemCollection[fullId] = newSystemCollection;
        } else {
          console.error('lineSystemCollection property does not exist: ' + fullId);
        }

        return newLineSystemCollection;

      });
    }
  }

  const invokeSystemDeActivation = async (idSet: IdSet) => {
    const [dealId, lineId, systemId] = idSet;
    const formData = {
      "operation": "DEACTIVATE"
    }

    const activateOptions = { ...genericPostOptions, body: JSON.stringify(formData) };
    updateSystemButtonCollection(idSet, false, SystemButton.return);

    fetch(appURL + `/systems/${systemId}/activations`, activateOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then(result => updateSystemDataFromServer(idSet))
            .catch(error => console.error('deactivateLicense catch: error', error));
        } else {
          const statusText = response.statusText ? `Text: ${response.statusText}` : '';
          setErrorDialog({ open: true, errorMessage: `Status: ${response.status} ${statusText}`, systemButton: SystemButton.return });
          console.error('deactivate !response.ok', response);
          updateSystemButtonCollection(idSet, true, SystemButton.return);
        }
      });
  }


  const updateSystemDataFromServer = async (idSet: IdSet) => {
    const [dealId, lineId, systemId] = idSet;
    const updatedSystemData: SystemActivationData = { hostId: '', hostId2: '', hostId3: '', hostType: HostType.hostName, hostType2: HostType.hostName, hostType3: HostType.hostName };
    fetch(appURL + `/systems/${systemId}`, authorizedRequestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then(result => {
              updatedSystemData.returnCount = result.current_return_count;
              updateSystemOnActivation(idSet, SystemUpdateReason.Deactivation, updatedSystemData)
            })
            .catch(error => console.error('deactivateLicense catch: error', error));
        } else {
          console.error('deactivate !response.ok', response);
          // TODO: change state somewhere do allow error to be shown
        }
      });
  }

  const getSystemDataFromServer = async (systemId: string): Promise<RawSystemsData | null> => {
    let result = null;
    try {
      const response = await fetch(appURL + `/systems/${systemId}`, authorizedRequestOptions);
      if (response.ok) {
        result = await response.json();
      }
    } catch (error) {
      console.error('getSystemDataFromServer catch: error', error);
    }
    return result;
  }

  const genericPutOptions: RequestInit = {
    method: "PUT", headers: {
      "Authorization": "Bearer " + authContext.authVariables?.access_token,
      "Content-Type": "application/json"
    },
  };

  const addUserAnnotationToSystem = (idSet: IdSet, user_annotation: string) => {
    const [dealId, lineId, systemId] = idSet;

    const formData = {
      "user_annotation": `${user_annotation}`
    }

    const addUserAnnotationToSystemOptions = { ...genericPutOptions, body: JSON.stringify(formData) };

    if (dealId && lineId) {
      fetch(appURL + `/systems/${systemId}`, addUserAnnotationToSystemOptions)
        .then((response: Response) => {
          if (response.ok) {
            updateSystemDataAfterUserAnnotationToSystem(dealId, lineId, systemId, user_annotation);
          } else {
            console.error('addUserAnnotationToSystem !response.ok', response);
          }
        });
    }
  }

  const updateSystemDataAfterUserAnnotationToSystem = (dealId: string, lineId: string, systemId: string, user_annotation: string) => {

    setSystemData((prevLineSystemCollection: LineSystemCollection) => {
      const newLineSystemCollection: LineSystemCollection = { ...prevLineSystemCollection };

      const prevSystemCollection: SystemCollection = prevLineSystemCollection[Utilities.getFullId(dealId, lineId)];
      const newSystemCollection: SystemCollection = { ...prevSystemCollection };

      const prevSystemData = prevSystemCollection[systemId];
      const newSystemData = { ...prevSystemData };

      newSystemData.user_annotation = user_annotation;

      newSystemCollection[systemId] = newSystemData;
      const fullId = Utilities.getFullId(dealId, lineId);
      if (newLineSystemCollection[fullId]) {
        newLineSystemCollection[fullId] = newSystemCollection;
      } else {
        console.error('lineSystemCollection property does not exist: ' + fullId);
      }

      return newLineSystemCollection;
    })
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const openSubscriptionDeal = (product: TrialProducts) => {
    const dealIdForProduct = saasDealIdCollection[product];
    if (dealIdForProduct) {
      handleDealOpen(dealIdForProduct, LinesView.CURRENT_SUBS, true);
    } else {
      // TODO: if user's only just subscribed, refresh deals...and then expand ccS1000D deal?
    }
  }

  const handleDealOpen = (id: string, view: LinesView, setOpen?: boolean) => {
    const closeOthers = setOpen !== undefined;
    let dealsSetter 
    if (view === LinesView.CURRENT_SUBS || view === LinesView.HISTORIC_SUBS){
      dealsSetter = setDealsSubsData;
    } else if (view === LinesView.CURRENT_EVALS || view === LinesView.HISTORIC_EVALS) {
      dealsSetter = setDealsEvalsData;
    } else {
      dealsSetter = setDealsData;
    }

    dealsSetter((prevDealsData) => {
      // update state of 'dealViewExpanded' property
      const dealsDataCopy = { ...prevDealsData };
      if (closeOthers) {
        Object.entries(dealsDataCopy).forEach(prop => {
          const [name, value] = prop;
          value['dealViewExpanded'] = false;
        });
      }
      let itemToUpdate = dealsDataCopy[id];
      const expandedPropertyToUpdate = itemToUpdate.dealViewExpanded;
      itemToUpdate['dealViewExpanded'] = setOpen ? true : !expandedPropertyToUpdate;
      // if deal has only one line and we're opening the deal - set the
      // lineViewExpanded property to 'true' also
      if (!expandedPropertyToUpdate && lineData[view][id]) {
        const dealLineCollection = lineData[view][id];
        const linesForDeal = Object.entries(dealLineCollection);
        if (linesForDeal.length === 1) {
          const newLineData = { ...lineData };
          const lineDataForDeal = newLineData[view][id];
          const firstEntry = Object.entries(lineDataForDeal)[0];
          const [name, value] = firstEntry;
          value['lineViewExpanded'] = true;
          setLineData(newLineData);
        }
      }
      return dealsDataCopy;
    });
  }

  const handleSortViewChange = (sortView: SortView, sortData: SortData) => {
    setSortViewData((prevSortViewData) => {
      const newSortViewData = { ...prevSortViewData };
      newSortViewData[sortView] = sortData;
      return newSortViewData;
    })
  }
  const handleLineOpen = (dealId: string, lineId: string, view: LinesView) => {
    setLineData((prevLineData) => {
      const lineDataCopy = { ...prevLineData };
      let dealItemToUpdate: InnerLineCollection = lineDataCopy[view][dealId];
      const lineItemToUpdate = dealItemToUpdate[lineId];
      const propertyToUpdate = lineItemToUpdate.lineViewExpanded;
      lineItemToUpdate['lineViewExpanded'] = !propertyToUpdate;
      return lineDataCopy;
    });
  }

  const handleErrorDialogClose = () => {
    setErrorDialog({ open: false, systemButton: SystemButton.activate, errorMessage: "" });
  }

  const handleDealsRefresh = () => {
    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        "Authorization": "Bearer " + authContext.authVariables?.access_token,
        "Accept": "application/json",
      }
    };
    updateSaasTrialState(requestOptions);
    getDeals(requestOptions);
    getUnactivatedSystem(requestOptions);
  }

  const handleDownloadsRefresh = () => {
    getDownloadProductData(authorizedRequestOptions);
  }

  const getDeals = async (requestOptions: RequestInit) => {
    setLicenseProgressNumber(1);
    setLicenseResponseState(prevResponseState => ({ ...prevResponseState, deals: { status: LicenseRequestStatus.inProgress } }));
    fetch(appURL + "/deals", requestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then(result => processAllDealData(requestOptions, result))
            .catch(error => console.error('getDeals error', error));
        } else if (response.status === 401) {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, deals: { status: LicenseRequestStatus.noItems } }));
        } else {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, deals: { status: LicenseRequestStatus.errorResponse, statusNumber: response.status } }));
        }
      }).catch(error => {
        setLicenseResponseState(prevResponseState => ({ ...prevResponseState, deals: { status: LicenseRequestStatus.errorResponse, statusNumber: 0 } }));
      });
  }
 
  const [evalRequestStatus,setEvalRequestStatus] = React.useState<EvalStatus>(EvalStatus.NotYetRequested)

  const history = useHistory();

  const requestEvaluation = async (evalData: EvalData) => {

    const formData = {
      "firstName": evalData.firstName,
      "lastName": evalData.lastName,
      "product": evalData.productName,
      "email": profileDetails.emailAddress,
      "account": evalData.compName
    }
    const startEvalPostOptions = { ...genericPostOptions, body: JSON.stringify(formData) };

    fetch(appURL + `/evaluation/start-eval`, startEvalPostOptions)
      .then((response: Response) => {
        if (response.ok) {
          history.push(NavigatorData.evaluationManagerBaseUrl);
          setEvalRequestStatus(EvalStatus.Success);
          setTimeout(() => {
            handleDealsRefresh();
          }, 8000);
        } else {
          setEvalRequestStatus(EvalStatus.Error);
        }
      }).catch(error => {
        console.error('requestEvaluation Error: Fetch failed' , error);
        setEvalRequestStatus(EvalStatus.InternalServerError);
      });
  }


  const startSubsTrial = async (product: TrialProducts, trialData: SubsTrialData) => {
    setSaasTrialStatusForProduct(product, SaaSTrialStatus.RequestingTrial);
    // generate unique deal for each saas trial:
    const subsTrialDealName = `SaaS-Subscriptions-${new Date().getTime()}`;

    const formData = {
      "dealId": subsTrialDealName,
      "firstName": trialData.firstName,
      "lastName": trialData.lastName,
      "product": product,
      "email": profileDetails.emailAddress,
      "account": trialData.compName,
      "lineNumber": 1
    }
    const startSubsTrialPostOptions = { ...genericPostOptions, body: JSON.stringify(formData) };
    fetch(appURL + `/subscription/start-trial`, startSubsTrialPostOptions)
      .then((response: Response) => {
        if (response.ok) {
          handleDealsRefresh();
        } else {
          setSaasTrialStatusForProduct(product, SaaSTrialStatus.Error);
        }
      }).catch(error => { 
        setSaasTrialStatusForProduct(product, SaaSTrialStatus.Error);
      });
  }
  /*
  Purpose: to update saasTrialStatus:
      Unknown,
      Ongoing, // we should have already got this from the 'isCurrent' property of the lineItems API
      Permitted,
      NotPermitted // this is set according to the response from this API call
  */
  const updateSaasTrialState = async (requestOptions: RequestInit) => {
    const pathEmail = Utilities.encodeParamForURI(authContext.authVariables?.email);
    const encodedURL = appURL + `/subscription/trial/${pathEmail}`;
    fetch(encodedURL, requestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then((result: TrialUsageData[]) => {
              if (Array.isArray(result)) {
                const newTrialStatusCollection = resetSaasTrialStatus(SaaSTrialStatus.Permitted);
                result.forEach((trialProduct) => {
                  newTrialStatusCollection[trialProduct.product] = calcTrialStateFromUsage(trialProduct);
                });
                setSaasTrialStatusCollection(newTrialStatusCollection);
              } else {
                console.error('updateSaasTrialState Error: Expected array in response');
                setSaasTrialStatusCollection(resetSaasTrialStatus(SaaSTrialStatus.Error));
              }
            })
            .catch(error => console.error('updateSaasTrialState error', error));
        } else if (response.status === 401) {
          console.error('updateSaasTrialState Error: 401');
          setSaasTrialStatusCollection(resetSaasTrialStatus(SaaSTrialStatus.Error));
        } else {
          console.error('updateSaasTrialState Error: unexpected response ' + response.status);
          setSaasTrialStatusCollection(resetSaasTrialStatus(SaaSTrialStatus.Error));
        }
      }).catch(error => {
        console.error('updateSaasTrialState Error: Fetch failed');
        setSaasTrialStatusCollection(resetSaasTrialStatus(SaaSTrialStatus.Error));
      });
  }

  const getActivations = async (idSet: IdSet) => {
    updateSystemButtonCollection(idSet, false, SystemButton.history);
    const [dealId, lineId, systemId] = idSet;
    setLicenseResponseState(prevResponseState => ({ ...prevResponseState, history: { status: LicenseRequestStatus.inProgress } }));
    fetch(appURL + "/systems/" + systemId + "/activations", authorizedRequestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then((result) => {
              const systemDataForHistoryRefresh: SystemActivationData = { hostId: '', hostId2: '', hostId3: '', hostType: HostType.hostName, hostType2: HostType.hostName, hostType3: HostType.hostName };
              if (result && Array.isArray(result)) {
                systemDataForHistoryRefresh['rawActivations'] = result;
                updateSystemOnActivation(idSet, SystemUpdateReason.RefreshSystemActivationData, systemDataForHistoryRefresh);
              }
            })
            .catch(error => console.error('getActivations error', error));
        } else if (response.status === 401) {
          updateSystemButtonCollection(idSet, true, SystemButton.history);
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, history: { status: LicenseRequestStatus.noItems } }));
        } else {
          console.error('Unexpected HTTP response:')
          const statusText = response.statusText ? `Text: ${response.statusText}` : '';
          setErrorDialog({ open: true, errorMessage: `Status: ${response.status} ${statusText}`, systemButton: SystemButton.history });
          updateSystemButtonCollection(idSet, true, SystemButton.history);
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, history: { status: LicenseRequestStatus.errorResponse, statusNumber: response.status } }));
        }
      });
  }

  function processAllDealData(requestOptions: RequestInit, result: any) {
    if (!Array.isArray(result)) {
      if (result.details) {
        // we don't yet fully know how error info will be passed back from server:
        console.error('Response has details:', result.details);
      } else {
        console.error('object is not an array - array expected', result);
      }
    } else {
      refreshAllDealData(requestOptions, result);
    }
  }

  function delayedFetch(requestOptions: RequestInit, url: string, delay: number, index: number, addParameter: boolean): Promise<Response> {
    const calculatedDelay = delay * index;
    // console.log('url:', url, 'delay:', calculatedDelay);
    //To ensure that the parameter isn't attach again to the /systems/ endpoint
    let parameterizedUrl: string;
    if (addParameter) {
      parameterizedUrl = url + `?issued=all`;
    } else {
      //Removed 'issued' parameter when calling /systems/ endpoint
      parameterizedUrl = url.replaceAll(/\?issued=(current|historic|all)/gm, "");
    }

    return new Promise(resolve => {
      setTimeout(() => {
        setLicenseProgressNumber(index + 1);
        return resolve(fetch(parameterizedUrl, requestOptions));
      }, calculatedDelay)
    });
  }

  const downloadjs = require('downloadjs');

  /*
    PreFetch all data:
    1. Get deals data
    2. For each deal:
      2a. Get lines data (via Promise[])
      2b. For each lines data response:
        2b1. Get systems data (via Promise[])
        2b2. For each systems data response:
          2b2a. Update systemData state
  */
  async function refreshAllDealData(requestOptions: RequestInit, result: DealType[]) {
    const dealCollection: DealCollection = {};
    const dealSubsCollection: DealCollection = {};
    const dealEvalsCollection: DealCollection = {};
    const newState = result.length === 0 ? LicenseRequestStatus.noItems : LicenseRequestStatus.dealsOK;
    setLicenseResponseState(prevResponseState => {
      if (prevResponseState.lines.status === LicenseRequestStatus.errorResponse) {
        return prevResponseState;
      } else {
        return {
          ...prevResponseState, deals: { status: newState }
        }
      }
    });

    result.forEach((item) => {
      if (item.dealType === SUBSCRIPTIONS_LICENSE_TYPE) {
        dealSubsCollection[item.dealId] = item;
      } else {
        if (item.dealId.slice(0,17) === EVALUATIONS_DEAL_IDENTIFIER){
          dealEvalsCollection[item.dealId] = item;
        } else {
          dealCollection[item.dealId] = item;
        }
      }
    });

    setDealsData(getStateUpdater(dealCollection));
    setDealsSubsData(getStateUpdater(dealSubsCollection));
    setDealsEvalsData(getStateUpdater(dealEvalsCollection));

    const licenseAndSubsDealEntries = Object.entries(dealCollection).concat(Object.entries(dealSubsCollection)).concat(Object.entries(dealEvalsCollection));

    const linesPromises = licenseAndSubsDealEntries.map((kvp, index) => {
      const [name, value] = kvp;
      const pathDealName = encodeURIComponent(name);
      const url = appURL + "/deals/" + pathDealName + "/lines";
      return delayedFetch(requestOptions, url, index === 0 ? 0 : delayForLinesConcurrency, index, true);
    });

    const newLineData: DealLineCollection = {};
    const newSubsLineData: DealLineCollection = {};
    const newEvalsLineData: DealLineCollection = {};
    const newLineDataHistoric: DealLineCollection = {};
    const newSubsLineDataHistoric: DealLineCollection = {};
    const newEvalsLineDataHistoric: DealLineCollection = {};
    setSystemData({});

    (linesPromises.length > 0) && await Promise.all(linesPromises).then((responses: Response[]) => {
    setLicenseResponseState(prevResponseState => ({ ...prevResponseState, lines: { status: LicenseRequestStatus.inProgress } }));
      let allIndex = -1;
      // iterate over each deal's '/lines' response
      responses.forEach((lineResponse, dealResponseIndex) => {
        if (lineResponse.status === 401) {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, lines: { status: LicenseRequestStatus.noItems } }));
          return console.error("system request: no Lines in response");
        } else if (!lineResponse.ok) {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, lines: { status: LicenseRequestStatus.errorResponse, statusNumber: lineResponse.status } }));
          return console.error("system request: error response");
        }
        lineResponse.text()
          .then(results => JSON.parse(results))
          .then(result => {
            if (!Array.isArray(result)) {
              if (result.details) {
                // we don't yet fully know how error info will be passed back from server:
                console.error('Response has details:', result.details);
              } else {
                console.error('object is not an array - array expected', result);
              }
            } else {
              const newState = result.length === 0 ? LicenseRequestStatus.noItems : LicenseRequestStatus.dealsOK;
              setLicenseResponseState(prevResponseState => {
                if (prevResponseState.lines.status === LicenseRequestStatus.errorResponse) {
                  return prevResponseState;
                } else {
                  return {
                    ...prevResponseState, lines: { status: newState }
                  }
                }
              });
              const dealId = getIdPartFromUrl(lineResponse, ResponsePart.deal);
              const resultLineData: LinesData[] = result as LinesData[];
              const linesDataObject: InnerLineCollection = {};
              const linesSubsDataObject: InnerLineCollection = {};
              const linesEvalsDataObject: InnerLineCollection = {};
              const linesDataObjectHistoric: InnerLineCollection = {};
              const linesSubsDataObjectHistoric: InnerLineCollection = {};
              const linesEvalsDataObjectHistoric: InnerLineCollection = {};
              const systemPromises: Promise<Response>[] = [];

              const systemsDelayData: DelayData[] = [];
              const products: TrialProducts[] = [];
              resultLineData.forEach((lineItem) => {
                allIndex++;
                const lineItemType = getLinesViewFromLine(lineItem);
                let targetCollection: InnerLineCollection = {};
                switch (lineItemType) {
                  case LinesView.CURRENT:
                    targetCollection = linesDataObject;
                    break;
                  case LinesView.CURRENT_SUBS:
                    products.push(lineItem.product as TrialProducts);
                    targetCollection = linesSubsDataObject;
                    break;
                  case LinesView.CURRENT_EVALS:
                    targetCollection = linesEvalsDataObject;
                    break;
                  case LinesView.HISTORIC:
                    targetCollection = linesDataObjectHistoric;
                    break;
                  case LinesView.HISTORIC_SUBS:
                    targetCollection = linesSubsDataObjectHistoric;
                    break;
                  case LinesView.HISTORIC_EVALS:
                    targetCollection = linesEvalsDataObjectHistoric;
                    break;
                }
                targetCollection[lineItem.lineNumber] = lineItem;
                const url = `${lineResponse.url}/${lineItem.lineNumber}/systems`; //Already previously encoded

                if (allIndex === 0) {
                  setLicenseProgressNumber(1);
                }
                systemsDelayData.push([url, allIndex]);
              }); // next lineItem
              setSaasDealIdCollection((prev) => {
                const newCollection = {...prev};
                products.forEach((product) => {newCollection[product] = dealId})
                return newCollection;
              });

              // now create an array of system-promises and then invoke those promises:
              systemsDelayData.forEach((data) => {
                const [url, requestNumber] = data;
                systemPromises.push(delayedFetch(requestOptions, url, delayForSystemConcurrency, requestNumber + 1, false));
              });
              invokeSystemPromises(systemPromises);
              newLineData[dealId] = linesDataObject;
              newSubsLineData[dealId] = linesSubsDataObject;
              newEvalsLineData[dealId] = linesEvalsDataObject;
              newLineDataHistoric[dealId] = linesDataObjectHistoric;
              newSubsLineDataHistoric[dealId] = linesSubsDataObjectHistoric;
              newEvalsLineDataHistoric[dealId] = linesEvalsDataObjectHistoric;
            }
          })
          .catch((error) => {
            setLicenseResponseState(prevResponseState => ({ ...prevResponseState, lines: { status: LicenseRequestStatus.errorResponse, statusNumber: lineResponse.status } }));
            console.error('refreshAllDealData error:', error);
          });
      }

      );

    }).catch((error) => {
      setLicenseResponseState(prevResponseState => ({ ...prevResponseState, lines: { status: LicenseRequestStatus.errorResponse, statusNumber: 0 } }));
      console.error('promise.all refreshAllDealData error:', error);
    });

    // can't easily keep state of arrowbuttons due to 
    // async nature not working with the setState callback!
    setLineData({
      [LinesView.CURRENT]: newLineData,
      [LinesView.CURRENT_SUBS]: newSubsLineData,
      [LinesView.CURRENT_EVALS]: newEvalsLineData,
      [LinesView.HISTORIC]: newLineDataHistoric,
      [LinesView.HISTORIC_SUBS]: newSubsLineDataHistoric,
      [LinesView.HISTORIC_EVALS]: newEvalsLineDataHistoric,
    });
  }

  const emptyCapabilitiesData: CapabilitiesData[] = [];
  const [capabilities, setCapabilities] = React.useState(emptyCapabilitiesData);
  const getAllCapabilities = async() => {
    const getRequestOptions: RequestInit = {
      method: 'GET',
      headers: {
        "Authorization": "Bearer " + authContext.authVariables?.access_token,
        "Accept": "application/json",
      }
    };
    const response = await fetch(appURL + '/capabilities', getRequestOptions);
    if (response.ok) {
      const capsObject: CapabilitiesData[] = await response.json();
      setCapabilities(capsObject);
    }
  }

  async function invokeSystemPromises(systemPromises: Promise<Response>[]) {
    if (systemPromises.length > 0) {
      setLicenseResponseState(prevResponseState => ({ ...prevResponseState, systems: { status: LicenseRequestStatus.inProgress } }));
    }
    await Promise.all(systemPromises).then((responses) => {
      responses.forEach((response) => {
        const dealId = getIdPartFromUrl(response, ResponsePart.deal);
        const lineId = getIdPartFromUrl(response, ResponsePart.line);
        const dealLineId = Utilities.getFullId(dealId, lineId);
        if (response.status === 401) {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, systems: { status: LicenseRequestStatus.noItems } }));
          return console.error("system request: no systems in response");
        } else if (!response.ok) {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, systems: { status: LicenseRequestStatus.errorResponse, statusNumber: response.status } }));
          return console.error("system request: error response");
        }
        response.text()
          .then(results => JSON.parse(results))
          .then(result => {
            if (!Array.isArray(result)) {
              if (result.details) {
                // we don't yet fully know how error info will be passed back from server:
                console.error('system request: esponse has details:', result.details);
              } else {
                console.error('system request: object is not an array - array expected', result);
              }
            } else {
              const newState = result.length === 0 ? LicenseRequestStatus.noItems : LicenseRequestStatus.dealsOK;
              setLicenseResponseState(prevResponseState => {
                if (prevResponseState.systems.status === LicenseRequestStatus.errorResponse) {
                  return prevResponseState;
                } else {
                  return {
                    ...prevResponseState, systems: { status: newState }
                  }
                }
              });
              const systemCollection: SystemCollection = {};

              result.forEach((item: RawSystemsData) => {                
                systemCollection[item.systemId] = populateSystemDataFromRawData(item);
              });

              setSystemData((prevSystemData) => {
                const newSystemData = { ...prevSystemData };
                newSystemData[dealLineId] = systemCollection;
                return newSystemData;
              });
            }
          })
          .catch(error => {
            setLicenseResponseState(prevResponseState => ({ ...prevResponseState, systems: { status: LicenseRequestStatus.errorResponse, statusNumber: response.status } }));
            console.error('invokeSystemPromises error', error);
          });
      });
    }).catch(error => {
      setLicenseResponseState(prevResponseState => ({ ...prevResponseState, systems: { status: LicenseRequestStatus.errorResponse, statusNumber: 0 } }));
      console.error('promise.all invokeSystemPromises error', error)
    });
  }

  const populateSystemDataFromRawData = (item: RawSystemsData) => {
    const internalSystemData: SystemsData = {
      "systemId": item.systemId,
      "fulfillmentSize": item.fulfillmentSize,
      "returnCountLimit": item.return_count_limit,
      "returnCount": item.current_return_count,
      "rawActivations": []
    };

    if (item.host_id) {
      const { hostType, hostId } = splitHostString(item.host_id);
      if (item.host_id === 'N/A') {
        internalSystemData['hostIdType'] = HostType.na;
        internalSystemData['hostId'] = '';
      } else {
        internalSystemData['hostIdType'] = hostType;
        internalSystemData['hostId'] = hostId;
      }
    }
    if (item.host_id2) {
      const { hostType, hostId } = splitHostString(item.host_id2);
      if (item.host_id2 === 'N/A') {
        internalSystemData['hostId2Type'] = HostType.na;
        internalSystemData['hostId2'] = '';
      } else {
        internalSystemData['hostId2Type'] = hostType;
        internalSystemData['hostId2'] = hostId;
      }
    }
    if (item.host_id3) {
      const { hostType, hostId } = splitHostString(item.host_id3);
      if (item.host_id3 === 'N/A') {
        internalSystemData['hostId3Type'] = HostType.na;
        internalSystemData['hostId3'] = '';
      } else {
        internalSystemData['hostId3Type'] = hostType;
        internalSystemData['hostId3'] = hostId;
      }
    }
    if (item.last_modified) {
      internalSystemData['lastModified'] = item.last_modified;
    }
    if (item.user_annotation) {
      internalSystemData['user_annotation'] = item.user_annotation;
    }
    return internalSystemData;
  }

  const getDownloadProductData = async (requestOptions: RequestInit) => {
    setLicenseResponseState(prevResponseState => ({ ...prevResponseState, downloads: { status: LicenseRequestStatus.inProgress } }));
    const downloadsPromise = fetch(appURL + `/downloads`, requestOptions);
    const response = await downloadsPromise.then(response => response);
    if (response.ok) {
      if (response.status === 200) {
        response.json().then(result => {
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, downloads: { status: LicenseRequestStatus.dealsOK } }));
          setDownloadProductsData(result);
        }).catch(error => {
          console.error('getDownloadProductData: json.then catch', error);
          setLicenseResponseState(prevResponseState => ({ ...prevResponseState, downloads: { status: LicenseRequestStatus.errorResponse, text: 'All downloads: Fetch request error' } }));
        });
      } else {
        // for case where status === 204 for when there are no licenses hence no downloads (undocumented in REST API) - but perhaps there are other cases as well?
        setLicenseResponseState(prevResponseState => ({ ...prevResponseState, downloads: { status: LicenseRequestStatus.noItems } }));
      }
    } else {
      response.text().then(text => {
        const errorText = 'All downloads error: ' + text;
        console.error('getDownloadProductData response!ok:', errorText);
        setLicenseResponseState(prevResponseState => ({ ...prevResponseState, downloads: { status: LicenseRequestStatus.errorResponse, statusNumber: response.status, text: errorText } }));
      });
    }
  }

  let downloadSections: NavigatorEntry[] = getDetails(downloadProductsData, true, true)!;
  let downloadLegacySections: NavigatorEntry[] = getDetails(downloadProductsData, true, false)!;
  let downloadServerSections: NavigatorEntry[] = getDetails(downloadProductsData, false)!;

  const downloadLicense = (dealId: string, lineNumber: string, systemId: string, licenseModel: string) => {
    const buttonStateForSystem = systemButtons[systemId];
    if (!(buttonStateForSystem === undefined || buttonStateForSystem.return)) {
      // do nothing if the return button is not enabled
    } else if (licenseModel !== LicenseType.redistribution) {
      fetch(appURL + `/systems/${systemId}`, authorizedRequestOptions)
        .then((response: Response) => {
          if (response.ok) {
            response.json().then(result => downloadjs(result.fnp_license, result.license_filename))
              .catch(error => console.error('License download failed.', error));
          } else {
            console.error('License download failed.', response);
          }
        });
    } else {
      const pathDealName = encodeURIComponent(dealId);
      const encodedURL = appURL + `/deals/${pathDealName}/lines/${lineNumber}/redist`;
      fetch(encodedURL, authorizedRequestOptions)
        .then((response: Response) => {
          if (response.ok) {
            response.json().then(result => downloadjs(result.code_license, result.code_filename))
              .catch(error => console.error('License download failed.', error));
          } else {
            console.error('License download failed.', response);
          }
        });
    }
  }


  const setSaasTrialStatusForProduct = (product: TrialProducts, state: SaaSTrialStatus) => {
    setSaasTrialStatusCollection((prevCollection) => {
      const newCollection = {...prevCollection};
      newCollection[product] = state;
      return newCollection;
    });
  }

  React.useEffect(() => {
    if (authContext.authVariables?.access_token) {
      const getRequestOptions: RequestInit = {
        method: 'GET',
        headers: {
          "Authorization": "Bearer " + authContext.authVariables?.access_token,
          "Accept": "application/json",
        }
      };
      getEvaluationAllowedProducts(getRequestOptions);
    }
  },[lineData[LinesView.HISTORIC_EVALS], lineData[LinesView.CURRENT_EVALS]]);

  const [evalAllowedProducts, setEvalAllowedProducts] = React.useState<MenuItemFormatType[]>();
  const [evalActivateDialogOpen, setEvalActivateDialogOpen] = React.useState<boolean>(false);
  const [evalUnactivatedDealId, setEvalUnactivatedDealId] = React.useState<string>("");
  const [evalUnactivatedLinesData, setEvalUnactivatedLinesData] = React.useState<LinesData | undefined>();
  const [evalUnactivatedSystemData, setEvalUnactivatedSystemData] = React.useState<SystemsData | undefined>();
  const [evalActivateStatus, setEvalActivateStatus] = React.useState(EvalStatus.NotYetRequested);

  const reInitializeEvalUnactivatedDataOnRefresh = async () => {
    setEvalActivateDialogOpen(false);
    setEvalUnactivatedDealId("");
    setEvalUnactivatedLinesData(undefined);
    setEvalUnactivatedSystemData(undefined);
    setEvalActivateStatus(EvalStatus.NotYetRequested);
  }
  
  const handleUnactivatedEvalDialogClose = () => {
    setEvalActivateDialogOpen(false);
  };

  const getEvaluationAllowedProducts = async (requestOptions: RequestInit) => {
    setEvalRequestStatus(EvalStatus.RequestOngoing);
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    let dateOneYearAgo = d.toJSON().slice(0, 10);

    const productFormatList: MenuItemFormatType[] = [
      { label: DownloadProductTypes.xmlCompareJava, value: DownloadProductTypes.xmlCompareJava },
      { label: DownloadProductTypes.xmlCompareRest, value: DownloadProductTypes.xmlCompareRest },
      { label: DownloadProductTypes.xmlMergeJava, value: DownloadProductTypes.xmlMergeJava },
      { label: DownloadProductTypes.xmlMergeRest, value: DownloadProductTypes.xmlMergeRest },
      { label: DownloadProductTypes.xmlDataCompareJava, value: DownloadProductTypes.xmlDataCompareJava },
      { label: DownloadProductTypes.ditaCompareJava, value: DownloadProductTypes.ditaCompareJava },
      { label: DownloadProductTypes.ditaCompareRest, value: DownloadProductTypes.ditaCompareRest },
      { label: DownloadProductTypes.ditaMergeJava, value: DownloadProductTypes.ditaMergeJava },
      { label: DownloadProductTypes.ditaMergeRest, value: DownloadProductTypes.ditaMergeRest },
      { label: DownloadProductTypes.docBookCompareJava, value: DownloadProductTypes.docBookCompareJava },
      { label: DownloadProductTypes.docbookCompareRest, value: DownloadProductTypes.docbookCompareRest },
      { label: DownloadProductTypes.deltaJSONJava, value: DownloadProductTypes.deltaJSONJava },
      { label: DownloadProductTypes.conversionQAJava, value: DownloadProductTypes.conversionQAJava },
      { label: DownloadProductTypes.conversionQARest, value: DownloadProductTypes.conversionQARest },
      { label: DownloadProductTypes.htmlCompareRest, value: DownloadProductTypes.htmlCompareRest }
    ]

    // returns a map of all products with their most current release-dates
    fetch(appURL + `/all-products`, requestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
            .then(((result) => {
              let evalAllowedProductList: MenuItemFormatType[] = productFormatList;

              Object.entries(lineData[LinesView.HISTORIC_EVALS]).forEach(kvp => {
                const [name, v] = kvp;
                  const lineItems = Object.entries(v);
                  lineItems.forEach(item => {
                    const [name, value] = item;
                    let index = Object.entries(result).findIndex(item => item[0] === value.product)
                    let release_date = Object.entries(result)[index][1];
                    if (typeof release_date === 'string') {
                      if (release_date < value.licenseExpiry && value.licenseExpiry > dateOneYearAgo) {
                        const indexOfObject = evalAllowedProductList.findIndex(object => {
                          return object.value === value.product
                        })
                        if (indexOfObject !== -1) {
                          evalAllowedProductList.splice(indexOfObject, 1)
                        }
                      }
                    }
                  })
              });

              Object.entries(lineData[LinesView.CURRENT_EVALS]).forEach(kvp => {
                const [name, v] = kvp;
                  const line_items = Object.entries(v)
                  line_items.forEach(item => {
                    const [name, value] = item
                    const indexOfObject = evalAllowedProductList.findIndex(object => {
                      return object.value === value.product
                    })
                    if (indexOfObject !== -1) {
                      evalAllowedProductList.splice(indexOfObject, 1)
                    }
                  })
              });
              setEvalAllowedProducts(evalAllowedProductList);
              setEvalRequestStatus(EvalStatus.ItemsPopulated);
            })
            ).catch(error => {
              setEvalRequestStatus(EvalStatus.Error);
            })
        } else {
          setEvalRequestStatus(EvalStatus.Error);
        }
      }).catch(error => {
        console.error('getEvaluationAllowedProducts Error: Fetch failed', error);
        setEvalRequestStatus(EvalStatus.InternalServerError);
      });
  }

  const populateDataForUnactivatedSystemAndOpenDialog = async (requestOptions: RequestInit, dealId: string, lineNumber: number): Promise<LinesData | null> => {
    let lineData = null;
    fetch(appURL + `/deals/${dealId}/lines/${lineNumber}`, requestOptions)
      .then((response: Response) => {
        if (response.ok) {
          response.json()
          .then((result: LinesData) => {
            setEvalUnactivatedLinesData(result);
            setEvalUnactivatedDealId(dealId);
            setEvalActivateDialogOpen(true);
            setEvalRequestStatus(EvalStatus.ItemsPopulated);
          })
        } else {
          setEvalRequestStatus(EvalStatus.Error);
        }
      })
      .catch(error => {
        console.error('setLineDataForUnactivatedSystem Error: Fetch failed', error);
        setEvalRequestStatus(EvalStatus.InternalServerError);
      });
    return lineData;
  }

 const getUnactivatedSystem = async (requestOptions: RequestInit) => {
    await reInitializeEvalUnactivatedDataOnRefresh();
    setEvalRequestStatus(EvalStatus.RequestOngoing);
    const pathEmail = Utilities.encodeParamForURI(authContext.authVariables?.email);
    const unactivatedSystemDataResponse = await fetch(appURL + `/systems/unactivated/${pathEmail}`, requestOptions);
    if (unactivatedSystemDataResponse.ok) {
      const result = await unactivatedSystemDataResponse.json();
      if (result.systemId !== undefined) {
        const dealId = result.parentDeal!;
        const lineNumber = result.parentLine!;
        setEvalUnactivatedSystemData(populateSystemDataFromRawData(result));        
        await populateDataForUnactivatedSystemAndOpenDialog(requestOptions, dealId, lineNumber);
      }
      setEvalRequestStatus(EvalStatus.ItemsPopulated);
    } else if (unactivatedSystemDataResponse.status === 500) {
      setEvalRequestStatus(EvalStatus.InternalServerError);
    } else {
      setEvalRequestStatus(EvalStatus.Error);
    }
  }

  const handleEvalActivateAndDownload = async (data: SystemActivationData) => {
    setEvalActivateStatus(EvalStatus.RequestOngoing);
    const idSet: IdSet = [evalUnactivatedDealId, evalUnactivatedLinesData?.lineNumber.toString()!, evalUnactivatedSystemData?.systemId!];

    invokeSystemActivation(idSet, data)
      .then(result => {
        setTimeout(() => {
          downloadLicense(evalUnactivatedDealId, evalUnactivatedLinesData?.lineNumber.toString()!, evalUnactivatedSystemData?.systemId!, evalUnactivatedLinesData?.licenseModel!);
          setEvalActivateStatus(EvalStatus.Success);
        }, 2000);
      })
      .catch(error => {
        setEvalActivateStatus(EvalStatus.InternalServerError);
      });
  }

  const productNames = downloadSections && downloadSections.map(section => section.id);
  const serverNames = downloadServerSections && downloadServerSections.map(section => section.id);
  const licensesDealLineCollection = linesView === LinesView.CURRENT ? lineData[LinesView.CURRENT] : lineData[LinesView.HISTORIC];
  const subsDealLineCollection = subsLinesView === LinesView.CURRENT_SUBS ? lineData[LinesView.CURRENT_SUBS] : lineData[LinesView.HISTORIC_SUBS];
  const evalsDealLineCollection = evalsLinesView === LinesView.CURRENT_EVALS ? lineData[LinesView.CURRENT_EVALS] : lineData[LinesView.HISTORIC_EVALS];

  //instantiate the classs of Cookies
  const redirectCookie = new Cookies();

  let content;
  if (authContext.isAuthenticated()) {
    switch (pageType) {
      case PageType.Home:
        content = <HomeContent />
        redirectCookie.set('redirectCookie', NavigatorData.baseUrl, {path:"/"})
        break;
      case PageType.Profile:
        content = <ProfileContent updateProfileMode={updateProfileMode} handleUpdateProfileMode={handleUpdateProfileMode} handleSaveProfileDetails={handleSaveProfileDetails} handleCancelProfileMode={handleCancelProfileMode} profileDetails={profileDetails} handleChange={handleProfileDetailsChange} />
        redirectCookie.set('redirectCookie', NavigatorData.profileBaseUrl, { path: '/' });
        break;
      case PageType.EvaluationManager:
        content = <LicensesContent licenseProgressNumber={licenseProgressNumber} dealsData={dealsEvalsData} dealLinesData={evalsDealLineCollection} linesSystemData={systemData} sortViewData={sortViewData} handleSortViewChange={handleSortViewChange} licenseRequestState={licenseResponseState} dateFormat={profileDetails.dateFormat} timeFormat={profileDetails.timeFormat} handleDealOpen={handleDealOpen} handleLineOpen={handleLineOpen} handleRefresh={handleDealsRefresh} systemButtons={systemButtons} callSystemAction={callSystemAction} createPartialFulfillmentSystem={createPartialFulfillmentSystem} downloadLicense={downloadLicense} addUserAnnotationToSystem={addUserAnnotationToSystem} handleChangeForLinesView={handleChangeForLinesView} linesView={evalsLinesView} enableExpiredViewOption={enableExpiredViewOption} 
        evalActivateDialogOpen={evalActivateDialogOpen} evalUnactivatedDealId={evalUnactivatedDealId} evalUnactivatedLine={evalUnactivatedLinesData} downloadProductsData={downloadProductsData} handleUnactivatedEvalDialogClose={handleUnactivatedEvalDialogClose} handleEvalActivateAndDownload={handleEvalActivateAndDownload} evalActivateStatus={evalActivateStatus} capabilities={capabilities}/>
        redirectCookie.set('redirectCookie', NavigatorData.evaluationManagerBaseUrl, {path:"/"})
        break;
      case PageType.LicenseManager:
        content = <LicensesContent licenseProgressNumber={licenseProgressNumber} dealsData={dealsData} dealLinesData={licensesDealLineCollection} linesSystemData={systemData} sortViewData={sortViewData} handleSortViewChange={handleSortViewChange} licenseRequestState={licenseResponseState} dateFormat={profileDetails.dateFormat} timeFormat={profileDetails.timeFormat} handleDealOpen={handleDealOpen} handleLineOpen={handleLineOpen} handleRefresh={handleDealsRefresh} systemButtons={systemButtons} callSystemAction={callSystemAction} createPartialFulfillmentSystem={createPartialFulfillmentSystem} downloadLicense={downloadLicense} addUserAnnotationToSystem={addUserAnnotationToSystem} handleChangeForLinesView={handleChangeForLinesView} linesView={linesView} enableExpiredViewOption={enableExpiredViewOption}
        capabilities={capabilities} />
        redirectCookie.set('redirectCookie', NavigatorData.licenseManagerBaseUrl, {path:"/"})
        break;
      // case PageType.SubscriptionManager:
      //   content = <LicensesContent licenseProgressNumber={licenseProgressNumber} dealsData={dealsSubsData} dealLinesData={subsDealLineCollection} linesSystemData={systemData} sortViewData={sortViewData} handleSortViewChange={handleSortViewChange} licenseRequestState={licenseResponseState} dateFormat={profileDetails.dateFormat} timeFormat={profileDetails.timeFormat} handleDealOpen={handleDealOpen} handleLineOpen={handleLineOpen} handleRefresh={handleDealsRefresh} systemButtons={systemButtons} callSystemAction={callSystemAction} createPartialFulfillmentSystem={createPartialFulfillmentSystem} downloadLicense={downloadLicense} addUserAnnotationToSystem={addUserAnnotationToSystem} handleChangeForLinesView={handleChangeForLinesView} linesView={subsLinesView} enableExpiredViewOption={enableExpiredViewOption} />
      //   redirectCookie.set('redirectCookie', NavigatorData.subscriptionManagerBaseUrl, {path:"/"})
      //   break;
      // case PageType.SaasProduct:
      //   content = <SaaSContent profileDetails={profileDetails} saasTrialStatusCollection={saasTrialStatusCollection} startSubsTrial={startSubsTrial} openSubscriptionDeal={openSubscriptionDeal} />
      //   redirectCookie.set('redirectCookie', NavigatorData.saasProductsBaseUrl, {path:"/"})
      //   break;
      case PageType.downloadDeltaNova:
        content = (productNames.includes(ProductId.deltaNova))
          ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadSections={downloadSections} productName={Products.deltaNova} dateFormat={profileDetails.dateFormat} />
          : <Redirect to={NavigatorData.downloadProductUrl} />
        break;
      case PageType.downloadDeltaInsights:
        content = (productNames.includes(ProductId.deltaInsights))
          ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadSections={downloadSections} productName={Products.deltaInsights} dateFormat={profileDetails.dateFormat} />
          : <Redirect to={NavigatorData.downloadProductUrl} />
        break;
      case PageType.downloadDeltaOxygenPlugin:
        content = (productNames.includes(ProductId.oxygenPlugin))
          ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadSections={downloadSections} productName={Products.oxygenPlugin} dateFormat={profileDetails.dateFormat} />
          : <Redirect to={NavigatorData.downloadProductUrl} />
        break;
        break;
      case PageType.downloadLinuxLicenseServer:
        content = (serverNames.includes(ProductId.linuxLicenseServer))
          ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadServerSections={downloadServerSections} downloadSections={downloadSections} productName={Products.linuxLicenseServer} dateFormat={profileDetails.dateFormat} />
          : <Redirect to={NavigatorData.downloadProductUrl} />
        break;
      case PageType.downloadWindowsLicenseServer:
        content = (serverNames.includes(ProductId.windowsLicenseServer))
          ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadServerSections={downloadServerSections} downloadSections={downloadSections} productName={Products.windowsLicenseServer} dateFormat={profileDetails.dateFormat} />
          : <Redirect to={NavigatorData.downloadProductUrl} />
        break;
        case PageType.downloadMacIntelLicenseServer:
          content = (serverNames.includes(ProductId.macIntelLicenseServer))
            ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadServerSections={downloadServerSections} downloadSections={downloadSections} productName={Products.macIntelLicenseServer} dateFormat={profileDetails.dateFormat} />
            : <Redirect to={NavigatorData.downloadProductUrl} />
          break;
        case PageType.downloadMacARMLicenseServer:
          content = (serverNames.includes(ProductId.macARMLicenseServer))
            ? <ProductInfoPage downloadProductsData={downloadProductsData} downloadServerSections={downloadServerSections} downloadSections={downloadSections} productName={Products.macARMLicenseServer} dateFormat={profileDetails.dateFormat} />
            : <Redirect to={NavigatorData.downloadProductUrl} />
          break;
        case PageType.downloadProducts:
          content = <DownloadProduct handleRefresh={handleDownloadsRefresh} responseState={licenseResponseState} downloadProductData={downloadProductsData} downloadServerSections={downloadServerSections} downloadSections={downloadSections} dateFormat={profileDetails.dateFormat} />
          redirectCookie.set('redirectCookie', NavigatorData.downloadProductUrl, {path:"/"})
        break;
        case PageType.downloadLegacyProducts:
          content = <DownloadLegacyProduct handleRefresh={handleDownloadsRefresh} responseState={licenseResponseState} downloadProductData={downloadProductsData} downloadSections={downloadLegacySections} dateFormat={profileDetails.dateFormat} />
          redirectCookie.set('redirectCookie', NavigatorData.legacyDownloadProductUrl, {path:"/"})
        break;
      }
    }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <CssBaseline />
        <nav className={classes.drawer}>
          <Hidden smUp implementation="js">
            <Navigator
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              downloadServerSections={downloadServerSections}
              downloadSections={downloadSections}
              downloadLegacySections={downloadLegacySections}
              profileDetails={profileDetails}
              handleRequestEvaluation={requestEvaluation}
              evalAllowedProducts ={evalAllowedProducts}
              evalRequestStatus={evalRequestStatus}
              licenseRequestState={licenseResponseState}
            />
          </Hidden>
          <Hidden xsDown implementation="css">
            <Navigator PaperProps={{ style: { width: drawerWidth } }} downloadServerSections={downloadServerSections} downloadSections={downloadSections} downloadLegacySections={downloadLegacySections}  profileDetails={profileDetails} handleRequestEvaluation={requestEvaluation} evalAllowedProducts ={evalAllowedProducts} evalRequestStatus={evalRequestStatus} licenseRequestState={licenseResponseState}/>
          </Hidden>
        </nav>
        <div className={classes.app}>
          <Header />
          <main className={pageType === PageType.LicenseManager || pageType === PageType.SubscriptionManager || pageType === PageType.EvaluationManager ? classes.main2 : classes.main}>
            {content}
          </main>
          {errorDialog.open &&
            <ErrorDialog open={errorDialog.open} handleClose={handleErrorDialogClose} systemButton={errorDialog.systemButton} errorMessage={errorDialog.errorMessage} />
          }
        </div>
      </div>
    </ThemeProvider>
  );
}

function calcTrialStateFromUsage(productTrialData: TrialUsageData) {
  let newTrialStatus: SaaSTrialStatus = SaaSTrialStatus.Unknown;
  if (productTrialData) {
    if (productTrialData.paidSubscriptionActive) {
      newTrialStatus = SaaSTrialStatus.PaidOngoing;
    } else {
      const isTrialOngoing = !Utilities.hasExpired(productTrialData.trialEnd);
      if (isTrialOngoing) {
        newTrialStatus = SaaSTrialStatus.Ongoing;
      } else if (productTrialData.trialAllowed) {
        newTrialStatus = SaaSTrialStatus.Permitted;
      } else {
        newTrialStatus = SaaSTrialStatus.NotPermitted;
      }
    }
  } else {
    // there's no trial data because this user has never started a Content Compare trial
    newTrialStatus = SaaSTrialStatus.Permitted;
  }
  return newTrialStatus;
}

function getStateUpdater(dealCollection: DealCollection) {
  const result = (prevDealsData: DealCollection) => {
    const prevDealEntries = Object.entries(prevDealsData);
    if (prevDealEntries.length === 0) {
      return dealCollection;
    } else {
      // use viewExpanded properties - if available
      Object.entries(dealCollection).forEach((kvp) => {
        const [name, newDealValue] = kvp;
        if (prevDealsData[name]) {
          newDealValue['dealViewExpanded'] = prevDealsData[name].dealViewExpanded;
        }
      });
      return dealCollection;
    }
  };
  return result;
}

function getLinesViewFromLine(lineItem: LinesData): LinesView {
  const isCurrent = lineItem.isCurrent;
  if (lineItem.licenseModel === LicenseType.saas_subscription || lineItem.licenseModel === LicenseType.saas_starter_subscription) {
    return isCurrent ? LinesView.CURRENT_SUBS : LinesView.HISTORIC_SUBS;
  } else if (lineItem.licenseType === EVALUATIONS_LICENSE_TYPE) {
    return isCurrent ? LinesView.CURRENT_EVALS : LinesView.HISTORIC_EVALS;
  } else {
    return isCurrent ? LinesView.CURRENT : LinesView.HISTORIC;
  }
}

function getIdPartFromUrl(lineResponse: Response, responsePart: ResponsePart) {
  const urlParts = lineResponse.url.split(responsePart);
  let dealId = '';
  if (urlParts.length === 2) {
    const dealWithLines = urlParts[1];
    const endPos = dealWithLines.indexOf('/');
    dealId = endPos !== -1 ? dealWithLines.substring(0, endPos) : dealWithLines;
  }
  return decodeURIComponent(dealId);
}

export default withStyles(styles)(Paperbase);